import { Component, OnInit, OnDestroy, AfterViewInit, Input, Output, EventEmitter, ChangeDetectorRef, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material';
import { Subscription } from 'rxjs';

import { FieldComponent, UndoAction } from './field.component';
import { FormField, Link, Disclaimer, PatternSegment } from './form-field.model';
import { AsperaService } from '../aspera/aspera.service';
import { FadeInAnimation } from '../shared/fade-in.animation';
import { ListItemAnimation } from '../shared/list-item.animation';
import { SelectFileDialogComponent } from '../shared/select-file-dialog.component';
import { TransferFile } from '../transfers/transfer-file.model';
import { ErrorDialogComponent } from '../shared/error-dialog.component';
import { DynamicOverlaysDirective } from './dynamic-overlays.directive';

@Component({
  selector: 'app-files-field',
  templateUrl: './files-field.component.html',
  styleUrls: ['./files-field.component.scss'],
  animations: [FadeInAnimation, ListItemAnimation()]
})
export class FilesFieldComponent extends FieldComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() draftData: any;
  @Input() dynamicOverlays: DynamicOverlaysDirective;
  @Input() field: FormField;
  @Input() locked = false;
  @Input() lockValues = false;
  @Input() templateData: any;
  @Input() workflowId: number;
  @Output() createFileSection = new EventEmitter<{fileId: string, section: any}>();
  @Output() focusChanged = new EventEmitter<boolean>();
  @Output() highlightChanged = new EventEmitter<{highlighted: boolean, color?: string}>();
  @Output() removeFileSection = new EventEmitter<string>();
  @Output() resized = new EventEmitter<FieldComponent>();
  @Output() returnKey = new EventEmitter<FieldComponent>();
  @Output() undoableAction = new EventEmitter<UndoAction>();
  @Output() updateLinked = new EventEmitter<{link: Link, value?: any, categories?: any[], enabled?: boolean}>();
  @Output() valueChanged = new EventEmitter<{field: string, value: any}>();
  @Output() viewLoaded = new EventEmitter<FieldComponent>();

  allowFiles = true;
  allowFolders = false;
  asperaRunning: boolean = this.asperaService.running;
  disclaimer: Disclaimer;
  displayPattern: string;
  fields: [[FormField]];
  filenamesValid = false;
  files: TransferFile[] = [];
  focused = false;
  highlightColor: string;
  highlighted = false;
  id = 'files';
  invalidCharacters = ['<', '>', ':', '"', '\'', '/', '\\', '|', '?', '*', '`', ',', '~', '%'];
  invalidCharactersPattern = new RegExp('^[^' + this.invalidCharacters.join().regExpEscape() + ']*$');
  max: number;
  min: number;
  linkedPattern: PatternSegment[];
  pattern: RegExp;
  touched = false;
  valid = false;
  private statusSub: Subscription | null = null;
  get fileBtnText(): string { return this.asperaService.buttonLabel('Select Files'); }
  get folderBtnText(): string { return this.asperaService.buttonLabel('Select Folders'); }
  get blank() { return this.files.length === 0; }
  get value() { return this.files; }

  constructor(
    cd: ChangeDetectorRef,
    private asperaService: AsperaService,
    private dialog: MatDialog,
    private ngZone: NgZone) { super(cd); }

  ngOnInit() {
    this.fields = this.field.fields;
    this.min = this.field.min;
    this.max = this.field.max;
    this.linkedPattern = this.field.linkedPattern;
    if (this.field.locked) { this.locked = true; }
    if (this.field.files != null) { this.allowFiles = this.field.files; }
    if (this.field.folders != null) { this.allowFolders = this.field.folders; }

    this.loadInitialState(this.draftData);

    this.statusSub = this.asperaService.status$.subscribe(() => {
      this.ngZone.run(() => {
        this.asperaRunning = this.asperaService.running;
        this.cd.markForCheck();
      });
    });
  }

  ngAfterViewInit() {
    this.viewLoaded.emit(this);
  }

  ngOnDestroy() {
    if (this.statusSub != null) { this.statusSub.unsubscribe(); }
  }

  loadAspera(onSuccess?: () => void, onFailure?: () => void) {
    this.asperaService.startSession(() => {
      this.asperaRunning = this.asperaService.running;
      if (this.asperaRunning) {
        if (onSuccess != null) { onSuccess(); }
      } else {
        if (onFailure != null) { onFailure(); }
      }
    });
  }

  loadInitialState(draftFiles?: TransferFile[]) {
    this.touched = false;

    this.files = draftFiles != null ? draftFiles : [];

    if (this.linkedPattern != null) {
      this.updatePattern();
    }

    // Emit initial validity and value
    this.validate();
    this.emitValue();
  }

  validate() {
    this.validateFilenames(this.files);
    this.valid = this.locked == true || (this.files.length > 0 &&
      (this.min == null || this.files.length >= this.min) &&
      (this.max == null || this.files.length <= this.max) &&
      this.filenamesValid);
  }

  validateFilenames(files) {
    let match = true;
    for (const file of files) {
      if (file.name.trim() === file.name // Disallow leading or trailing whitespace
        && this.invalidCharactersPattern.test(file.name)
        && (this.pattern == null || this.pattern.test(file.name))) {
        file.status = 'valid';
      } else {
        file.status = 'invalid';
        match = false;
      }
    }
    this.filenamesValid = match;
  }

  onSelectFiles() {
    if (this.asperaRunning) {
      this.selectFiles();
    } else {
      this.loadAspera(
        () => this.selectFiles(),
        () => this.asperaService.showDesktopModal()
      );
    }
  }

  selectFiles() {
    const dialogRef = this.dialog.open(SelectFileDialogComponent, {
      height: '240px',
      width: '320px',
    });
    const allowMultiple = this.max !== 1;
    this.asperaService.selectFiles(allowMultiple).then((files: TransferFile[]) => {
      if (files.length > 0) {
        this.files = this.files.concat(files);
        this.cd.markForCheck();
        this.validate();
        this.emitValue();
        setTimeout(() => this.resized.emit(this), 100);
        if (this.fields != null) {
          for (const file of files) {
            this.createFileSection.emit({
              fileId: file.id,
              section: {
                name: file.name,
                rows: this.fields
              }
            });
          }
        }
      }
      this.touched = true;
      dialogRef.close();
    }).catch(() => {
      dialogRef.close();
    });
  }

  onSelectFolders() {
    if (this.asperaRunning) {
      this.selectFolders();
    } else {
      this.loadAspera(
        () => this.selectFolders(),
        () => this.asperaService.showDesktopModal()
      );
    }
  }

  selectFolders() {
    const dialogRef = this.dialog.open(SelectFileDialogComponent, {
      height: '240px',
      width: '320px',
      data: { folders: true }
    });
    const allowMultiple = this.max !== 1;
    this.asperaService.selectFolders(allowMultiple).then((files: TransferFile[]) => {
      if (files.length > 0) {
        this.files = this.files.concat(files);
        this.cd.markForCheck();
        this.validate();
        this.emitValue();
        setTimeout(() => this.resized.emit(this), 100);
        if (this.fields != null) {
          for (const file of files) {
            this.createFileSection.emit({
              fileId: file.id,
              section: {
                name: file.name,
                rows: this.fields
              }
            });
          }
        }
      }
      this.touched = true;
      dialogRef.close();
    }).catch(() => {
      dialogRef.close();
    });
  }

  removeFile(fileId: any) {
    this.valid = false;
    if (this.fields != null) {
      this.removeFileSection.emit(fileId);
    }
    const index = this.files.findIndex((f) => f.id === fileId);
    this.files.splice(index, 1);
    this.touched = true;
    this.validate();
    this.emitValue();
    setTimeout(() => this.resized.emit(this), 100);
  }

  emitValue() {
    this.valueChanged.emit({field: this.id, value: this.files});
  }

  setValue(value: any, emit: boolean = true) {
    this.files = value;
    this.validate();
    if (emit) {
      this.emitValue();
    }
  }

  setPreviousValue() { }

  resetPreviousValue() { }

  positionOverlays() { }

  reset() {
    for (const file of [...this.files]) {
      this.removeFile(file.id);
    }
    this.loadInitialState();
    this.cd.markForCheck();
  }

  compare(other): number { return 0; }

  updateLinkedFields() { }

  setCategories(category) { }

  setPatternValue(field: string, value: any) {
    if (this.linkedPattern == null) { return; }
    const segment = this.linkedPattern.find((s) => s.field === field);
    if (segment != null) {
      if (value != null && value.hasOwnProperty('displayed')) {
        value = value.displayed;
      }
      if (value != null) {
        // If the value includes a segment in parentheses, use only that segment
        const match = value.match(/\(([^\(\)]*)\)$/);
        if (match != null) {
          value = match[1];
        }
        value = value.toLowerCase();
      }
      segment.value = value;
      this.updatePattern();
      this.validate();
    }
  }

  updatePattern() {
    let pattern = '^'; // add start-of-string anchor
    let display = '';
    let fileExtensionIsFlexible = false;

    for (const segment of this.linkedPattern) {
      if (segment.optional && (segment.value == null || segment.value == '')) {
        if (segment.field === 'file_extension') {
          fileExtensionIsFlexible = true;
          continue; // Skip optional segments that have no value, but mark file extension as optional
        }
        continue;
      }

      if (segment.prefix != null) {
        pattern += segment.prefix.regExpEscape();
        display += segment.prefix;
      }

      if (segment.value != null) {
        pattern += segment.value.regExpEscape();
        display += segment.value;
      } else {
        pattern += '.+';
        display += '•••';
      }
    }

    if (fileExtensionIsFlexible) {
      pattern += '\\..+'; // If file extension is optional, only require a . and non-empty extension
    }

    pattern += '$';

    this.displayPattern = display;
    this.pattern = new RegExp(pattern);
    this.cd.markForCheck();
  }

  undo(action: UndoAction) { }

  setLockValues(state) { }

  showErrorDialog(message) {
    const dialogRef = this.dialog.open(ErrorDialogComponent, {
      height: '240px',
      width: '320px',
      data: {message: message}
    });
    setTimeout(() => dialogRef.close(), 6000);
  }

  valuesEqual(a, b): boolean {
    if (a == null || b == null) {
      return a == b;
    } else if (a.length != b.length) {
      return false;
    } else {
      for (let i = 0 ; i < a.length ; i++) {
        if (!this.fileValuesEqual(a[i], b[i])) {
          return false;
        }
      }
      return true;
    }
  }

  fileValuesEqual(a, b): boolean {
    a = a != null ? a.path : null;
    b = b != null ? b.path : null;
    if (a == null || b == null) {
      return a == b;
    } else {
      return a.caseInsensitiveCompare(b);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/forms/files-field.component.ts